import type {
  AdditionalFaq,
  FaqStatus,
  FaqSyncState,
} from '../../types/knowledgeQuestions';
import type { Tone } from '../../ui/v2';

export const KNOWLEDGE_QUESTIONS_BASE = '/knowledge-questions';
export const KNOWLEDGE_BASE_HREF = '/store-info/knowledge-base';
/** Every Additional FAQ with its answer, opened from the Knowledge Base card. */
export const ADDITIONAL_FAQS_HREF = `${KNOWLEDGE_BASE_HREF}/faqs`;

/** Newest first, so a question just added sits at the top. */
export function sortFaqs(faqs: AdditionalFaq[]): AdditionalFaq[] {
  return [...faqs].sort(
    (a, b) => b.created_at.localeCompare(a.created_at) || b.id - a.id,
  );
}

export type FaqFilter = 'all' | 'live' | 'draft';

/** Live means Arthur uses it on calls; everything else is a draft. */
export const faqMatches = (faq: AdditionalFaq, filter: FaqFilter) =>
  filter === 'all' || (filter === 'live') === faq.is_live;

/**
 * What a list row says about an FAQ: whether Arthur uses it (a solid green
 * Live) and, for drafts, where it stands. `note` flags a live answer with
 * edits that aren't published yet.
 */
export function faqListState(faq: AdditionalFaq): {
  label: string;
  tone: Tone;
  note?: string;
} {
  if (faq.is_live) {
    return {
      label: 'Live',
      tone: 'success',
      note: faq.has_unpublished_changes ? 'Edit not live' : undefined,
    };
  }
  switch (faq.status) {
    case 'checking':
      return { label: 'Checking', tone: 'accent' };
    case 'conflict':
      return { label: 'Conflict', tone: 'danger' };
    case 'uncertain':
      return { label: 'Needs a look', tone: 'warning' };
    case 'ready':
      return { label: 'Ready to publish', tone: 'accent' };
    case 'with_support':
      return { label: 'With support', tone: 'accent' };
    default:
      return { label: 'Draft', tone: 'neutral' };
  }
}

export const isChecking = (faq: { status: FaqStatus } | null | undefined) =>
  faq?.status === 'checking';

/** Calls page filtered to the Retell call this question came from. */
export function callHref(retellCallId: string): string {
  return `/calls?call_id=${encodeURIComponent(retellCallId)}`;
}

export function canPublishRole(
  role: string | undefined,
  isPlatformAdmin: boolean,
): boolean {
  return isPlatformAdmin || role === 'Owner' || role === 'Manager';
}

/**
 * Why Publish is unavailable, or null when it can go. Publish saves and
 * checks an edit itself, so unsaved text never blocks it. Staff never see
 * the button; this covers everyone who can.
 */
export function publishBlockedReason(
  faq: AdditionalFaq,
  opts: { dirty: boolean },
): string | null {
  switch (faq.status) {
    case 'with_support':
      return WITH_SUPPORT_REASON;
    case 'archived':
      return 'This FAQ was removed.';
  }
  if (opts.dirty) return null;
  switch (faq.status) {
    case 'published':
      return 'Arthur already has this answer.';
    case 'conflict':
      return 'Edit the answer to fix the conflict.';
    case 'uncertain':
      return 'Edit the answer so it can be checked again.';
    default:
      return null;
  }
}

/** A passing check this recent is trusted at Publish; an older one runs again. */
export const CHECK_FRESH_MS = 20 * 60_000;

/** Backend ``knowledge_gaps.support.APPROVED_SUMMARY``. */
export const SUPPORT_APPROVED_SUMMARY = 'Approved by AskArthur support';

/**
 * Whether the current check result is a support approval. An approval
 * writes this summary; any edit clears the check and any later check
 * overwrites it, so it covers only the text support saw. The request's
 * `resolution` stays 'approved' through later edits, so it can't tell.
 */
export const isSupportApproved = (faq: AdditionalFaq) =>
  faq.check_verdict === 'no_conflict' &&
  faq.check_summary === SUPPORT_APPROVED_SUMMARY;

/**
 * Whether Publish can skip the conflict check and go straight to confirming.
 * A support approval stays good: checking again would re-flag what support
 * already cleared.
 */
export function isCheckFresh(faq: AdditionalFaq, now = Date.now()): boolean {
  if (faq.status !== 'ready') return false;
  if (isSupportApproved(faq)) return true;
  if (!faq.checked_at) return false;
  return now - new Date(faq.checked_at).getTime() < CHECK_FRESH_MS;
}

/**
 * A publish refused because the passing check no longer covers the answer
 * (backend ``stale_reason``, a 409). Only this calls for checking again;
 * any other failure is retried by publishing again.
 */
export function isStaleCheckError(error: {
  status: number | null;
  message: string;
}): boolean {
  return (
    error.status === 409 &&
    /since the last check|Run the conflict check/.test(error.message)
  );
}

/** What the check reads (backend ``knowledge_gaps.corpus.full_corpus``). */
export const CHECK_SOURCES = [
  'Menu',
  'Hours',
  'Store info',
  'Deals',
  'Other FAQs',
] as const;

export const WITH_SUPPORT_REASON =
  'AskArthur support has this answer. You can edit, check or publish it once they reply.';

export const isWithSupport = (faq: { status: FaqStatus } | null | undefined) =>
  faq?.status === 'with_support';

/** The check flagged this answer, so the store can hand it to support. */
export function canContactSupport(faq: AdditionalFaq): boolean {
  return (
    (faq.check_verdict === 'conflict' || faq.check_verdict === 'uncertain') &&
    (faq.status === 'conflict' || faq.status === 'uncertain')
  );
}

/** A support decline note, shown until the answer passes a check again. */
export function declinedNote(faq: AdditionalFaq): string | null {
  const req = faq.support_request;
  if (req?.resolution !== 'declined') return null;
  const stillOpen = ['draft', 'checking', 'conflict', 'uncertain'].includes(
    faq.status,
  );
  return stillOpen ? req.resolution_note : null;
}

export function syncTone(sync: FaqSyncState | null): Tone {
  switch (sync?.status) {
    case 'failed':
      return 'danger';
    case 'waiting_for_kb':
      return 'warning';
    case 'pending':
    case 'syncing':
      return 'accent';
    default:
      return 'success';
  }
}

export const occurrenceLabel = (n: number) =>
  n === 1 ? 'Asked once' : `Asked ${n} times`;
